import { split } from 'ramda'
import { arrDec, arrMaxF } from '../fun-js-lib/arr'
import { canvasFonted, canvasTextBounded } from '../fun-js-lib/canvas'
import Dot from '../fun-js-lib/geometry/Dot'
import { strsEmpty } from '../fun-js-lib/str'

export const emptyA4Canvas = () => {
  let canvas = document.createElement('canvas')
  canvas.width = 2970
  canvas.height = 2100
  return canvas
}

/**
 * @param {HTMLCanvasElement} canvas 
 */
export const fillWhiteBackground = canvas => {
  let context = canvas.getContext('2d')
  fillStyled(context, 'white', () =>
    context.fillRect(0, 0, canvas.width, canvas.height))
}

/**
 * Changes context fill style, calls `f`, then reverts context fill style
 * @param {CanvasRenderingContext2D} context 
 * @param {string} style 
 * @param {() => any} f 
 */
export const fillStyled = (context, style, f) => {
  let temp = context.fillStyle
  context.fillStyle = style
  f()
  context.fillStyle = temp
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {string} text 
 * @param {number} x
 * @param {number} y
 * @param {string} style 
 */
export function fillTextStyled (context, text, x, y, style) {
  fillStyled(context, style, () => {
    context.fillText(text, x, y)
  })
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {string} text 
 */
export const textWidth = (context, text) =>
  context.measureText(text).width

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {string} font 
 * @param {string} text 
 */
export function textWidthFonted (context, font, text) {
  let width = 0
  canvasFonted(context, font, () => {
    width = textWidth(context, text)
  })
  return width
}

/**
 * 
 * @param {CanvasRenderingContext2D} context 
 * @param {Dot} dot1 
 * @param {Dot} dot2 
 * @param {number} lineWidth 
 */
export function drawLine (context, dot1, dot2, lineWidth) {
  context.strokeStyle = 'black'
  context.beginPath()
  context.moveTo(dot1.x, dot1.y)
  context.lineTo(dot2.x, dot2.y)
  context.lineWidth = lineWidth
  context.stroke()
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {Rect} rect 
 */
export function drawZLine (context, rect) {
  let dot1 = new Dot({ x: rect.x, y: rect.y })
  let dot2 = new Dot({ x: rect.x + rect.w, y: rect.y })
  let dot3 = new Dot({ x: rect.x, y: rect.y + rect.h })
  let dot4 = new Dot({ x: rect.x + rect.w, y: rect.y + rect.h })
  drawLine(context, dot1, dot2, 2)
  drawLine(context, dot2, dot3, 2)
  drawLine(context, dot3, dot4, 2)
}

/**
 * Draws diagonal from bottom left corner to upper right corner of `rect`
 * @param {CanvasRenderingContext2D} context 
 * @param {Rect} rect
 */
export function drawDiagonal (context, rect) {
  let start = new Dot({ x: rect.x, y: rect.y + rect.h })
  let end = new Dot({ x: rect.x + rect.w, y: rect.y })
  drawLine(context, start, end, 1)
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {number} x 
 * @param {number} y 
 * @param {number} w 
 * @param {number} h 
 * @param {string} color 
 */
export const rectColored = (context, x, y, w, h, color) =>
  fillStyled(context, color, () =>
    context.fillRect(x, y, w, h))

/**
 * Fills text so that it fits into rect 
 * @param {CanvasRenderingContext2D} context 
 * @param {string} text 
 * @param {Rect} rect 
 */
export function fillTextIntoRect (context, text, rect) {
  const maxFontSize = 44
  let optimalFontSize = arrDec(maxFontSize + 1)
    .find(fontSize => {
      let maxWordW = 0
      let nLines = 0
      canvasFonted(context, fontSize + 'px Arial', () => {
        maxWordW = maxWordWidth(context, text)
        nLines = splitBounded(context, text, rect.w).length
      })
      let height = fontSize * nLines
      return maxWordW <= rect.w && height <= rect.h
    })
  canvasFonted(context, optimalFontSize + 'px Arial', () => {
    const lineInterval = optimalFontSize / 1.2
    canvasTextBounded(context, text, rect, lineInterval)
  })
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {string} text 
 */
export function maxWordWidth (context, text) {
  let words = split(' ', text)
  return arrMaxF(words, word => textWidth(context, word))
}

/**
 * Using `maxW` as maximum row width, splits `text` by spaces so that
 * strings can fit in these rows. If `text` is an empty string,
 * empty array is returned
 * @param {CanvasRenderingContext2D} context 
 * @param {string} text 
 * @param {number} maxW 
 */
export function splitBounded (context, text, maxW) {
  if (text === '') {
    return []
  } else {
    let words = text.split(' ')
    let w = 0
    let lines = strsEmpty()
    let line = strsEmpty()
    words.forEach(word => {
      let wordW = textWidth(context, word)
      w += wordW + textWidth(context, ' ')
      if (w > maxW) {
        if (line.length !== 0) {
          lines.push(line.join(' '))
        }
        line = [word]
        w = wordW
      } else {
        line.push(word)
      }
    })
    lines.push(line.join(' '))
    return lines
  }
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {string} text 
 * @param {number} maxW 
 * @param {string} font 
 */
export function splitBoundedFonted (context, text, maxW, font) {
  let lines = strsEmpty()
  canvasFonted(context, font, () => {
    lines = splitBounded(context, text, maxW)
  })
  return lines
}

/**
 * @param {CanvasRenderingContext2D} context 
 * @param {Rect} rect
 * @param {number} lineWidth
 */
export const drawFrame = (context, rect, lineWidth) => {
  context.fillRect(rect.x, rect.y, rect.w, rect.h)
  fillStyled(context, 'white', () =>
    context.fillRect(
      rect.x + lineWidth,
      rect.y + lineWidth,
      rect.w - 2 * lineWidth,
      rect.h - 2 * lineWidth))
}